import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  Contract,
  ContractBody,
  ContractModality,
  ContractType,
  Displacement,
  DisplacementBody,
  ExtraValue,
  ExtraValueBody,
  Invoice,
  InvoiceCreateBody,
  LooseService,
  LooseServiceBody,
} from '@erp/shared';

const BILLING_KEY = ['erp', 'billing'] as const;

// ── Tipos de contrato ───────────────────────────────────────────────────────

export interface ContractTypeCreateBody {
  name: string;
  modality: ContractModality;
}

export function useCreateContractType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ContractTypeCreateBody) => {
      const res = await api<ContractType>('/billing/contract-types', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export interface ContractTypeUpdateBody {
  name: string;
  modality: ContractModality;
  active: boolean;
}

export function useUpdateContractType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: ContractTypeUpdateBody }) => {
      const res = await api<ContractType>(`/billing/contract-types/${id}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

// ── Contratos ───────────────────────────────────────────────────────────────

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ContractBody) => {
      const res = await api<Contract>('/billing/contracts', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useUpdateContract(contractId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ContractBody) => {
      const res = await api<Contract>(`/billing/contracts/${contractId}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useCancelContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contractId: number) => {
      const res = await api<Contract>(`/billing/contracts/${contractId}/cancel`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

// ── Cadastros financeiros (v9): deslocamentos, serviços avulsos, valores ────
// extras — save (POST sem id / PUT com id) + delete; escrita exige
// contracts.manage (a API barra; o front só esconde os botões).

export function useSaveDisplacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number | null; body: DisplacementBody }) => {
      const res = id
        ? await api<Displacement>(`/billing/displacements/${id}`, { method: 'PUT', body })
        : await api<Displacement>('/billing/displacements', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useDeleteDisplacement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/billing/displacements/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useSaveLooseService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number | null; body: LooseServiceBody }) => {
      const res = id
        ? await api<LooseService>(`/billing/loose-services/${id}`, { method: 'PUT', body })
        : await api<LooseService>('/billing/loose-services', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useDeleteLooseService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/billing/loose-services/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useSaveExtraValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number | null; body: ExtraValueBody }) => {
      const res = id
        ? await api<ExtraValue>(`/billing/extra-values/${id}`, { method: 'PUT', body })
        : await api<ExtraValue>('/billing/extra-values', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useDeleteExtraValue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/billing/extra-values/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

// ── Faturas ─────────────────────────────────────────────────────────────────

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: InvoiceCreateBody) => {
      const res = await api<Invoice>('/billing/invoices', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function usePayInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: number) => {
      const res = await api<Invoice>(`/billing/invoices/${invoiceId}/pay`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

export function useReverseInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: number) => {
      const res = await api<Invoice>(`/billing/invoices/${invoiceId}/reverse`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}
